import React, { useEffect, useState } from 'react';
import axiosInstance from '../request';
import { URL } from "../api";
import {
  Button,
  Input,
  Form,
  Label,
  Modal,
  ModalHeader,
  ModalBody
} from "reactstrap";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";


const TodayEmiSchedule = () => {
  const [emis, setEmis] = useState([]);
  const [filterType, setFilterType] = useState('');
  const [filterValue, setFilterValue] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [data, setData] = useState([]);
  
const [modalOpen, setModalOpen] = useState(false);
const [selectedRow, setSelectedRow] = useState(null);
const [amount, setAmount] = useState("");
const [paymentLink, setPaymentLink] = useState("");
const [payLoading, setPayLoading] = useState(false);
const [currentPage, setCurrentPage] = useState(1);
const rowsPerPage = 10;
const [hasNextPage, setHasNextPage] = useState(true);

const companyCode = localStorage.getItem("companyCode");
const branchCode = localStorage.getItem("branch_code");



 const fetchEmis = async (page = 1) => {
  try {
    const params = {
      page: page - 1,   
      size: rowsPerPage,
      companyCode: companyCode || null,
      branchCode: companyCode ? branchCode : null 
    };

    if (filterType && filterValue) {
      if (filterType === 'customer_id') params.customerId = filterValue;
      else if (filterType === 'loan_id') params.loanId = filterValue;
      else if (filterType === 'emi_id') params.emiId = filterValue;
    }

    if (fromDate) params.fromDate = fromDate;
    if (toDate) params.toDate = toDate;

    const res = await axiosInstance.get(URL.GET_TODAY_EMI, { params });

    const data = Array.isArray(res.data) ? res.data : [];
    setEmis(data);
    setCurrentPage(page);

    // if returned rows < page size → no next page done
    setHasNextPage(data.length === rowsPerPage);

  } catch (error) {
    console.error("Failed to fetch EMI schedules:", error);
    setEmis([]);
  }
};


useEffect(() => {
  fetchEmis(1);
}, []);


  const openPaymentPopup = (row) => {
  setSelectedRow(row);

  // For today's EMI → outstanding amount only
  const outstanding = Number(row.outstanding_amount || 0);
  setAmount(outstanding);

  setPaymentLink("");
  setModalOpen(true);
};



const handleGenerateLink = async () => {
  if (!amount) {
    toast.error("Enter amount first!");
    return;
  }

  setPayLoading(true);
  try {
    const res = await axiosInstance.get(URL.PAYMENT_LINK, {
      params: {
        customerId: selectedRow.customer_id,
        loanId: selectedRow.loan_id,
        amount,
        callbackUrl: "https://peday.money/"
      }
    });

    const link =
      res.data.short_url || res.data.payment_link || res.data.link || "";

    setPaymentLink(link);
    toast.success("Payment link generated!");
  } catch (e) {
    toast.error("Failed to generate link");
  } finally {
    setPayLoading(false);
  }
};

const exportAllByPaging = async () => {
  try {
    let allData = [];
    let page = 0;
    const size = 50; // 🔥 key change
    let hasMore = true;

    toast.info("Export started, please wait...");

    while (hasMore) {
      const res = await axiosInstance.get(URL.GET_TODAY_EMI, {
        params: {
          page,
          size,
          ...(filterType === "customer_id" && { customerId: filterValue }),
          ...(filterType === "loan_id" && { loanId: filterValue }),
          ...(filterType === "emi_id" && { emiId: filterValue }),
          ...(fromDate && { fromDate }),
          ...(toDate && { toDate })
        }
      });

      const rows = Array.isArray(res.data) ? res.data : [];

      if (rows.length === 0) break;

      allData.push(...rows);

      // 🛑 hard safety limit
      if (allData.length > 5000) {
        toast.error("Too much data. Please apply filters.");
        return;
      }

      hasMore = rows.length === size;
      page++;
    }

    if (allData.length === 0) {
      toast.info("No data to export");
      return;
    }

    const ws = XLSX.utils.json_to_sheet(allData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Today EMI Report");

    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, "TodayEmi_Report.xlsx");

    toast.success(`Exported ${allData.length} rows`);

  } catch (e) {
    console.error(e);
    toast.error("Export failed");
  }
};




const handleSendLink = async () => {
  if (!amount) {
    toast.error("Enter amount first!");
    return;
  }

  setPayLoading(true);
  try {
    await axiosInstance.get(URL.PAYMENT_LINK, {
      params: {
        customerId: selectedRow.customer_id,
        loanId: selectedRow.loan_id,
        amount,
        callbackUrl: "https://peday.money/"
      }
    });
    toast.success("Payment link sent!");
  } catch (e) {
    toast.error("Failed to send link");
  } finally {
    setPayLoading(false);
  }
};

const handleCopy = () => {
  navigator.clipboard.writeText(paymentLink);
  toast.info("Copied to clipboard!");
};


const handleKeyDown = (e) => {
  if (e.key === 'Enter') {
    fetchEmis(1); // explicitly reset page etc
  }
};




  return (
    <div className="container">
      <h3 className="mb-4">Today's EMI Schedule</h3>

      {/* Filter Section */}
      <div className="card mb-4 p-3">
        <div className="row gx-2 gy-2 align-items-end">

          {/* Filter Type */}
          <div className="col-auto">
            <label className="form-label">Filter Type</label>
            <select
              className="form-select form-select-sm"
              value={filterType}
              onChange={e => {
                setFilterType(e.target.value);
                setFilterValue('');
              }}
            >
              <option value="">Select</option>
              <option value="customer_id">Customer ID</option>
              <option value="loan_id">Loan ID</option>
              <option value="emi_id">EMI ID</option>
            </select>
          </div>

          {/* Filter Value */}
          {filterType && (
            <div className="col-auto">
              <label className="form-label">
                {filterType === 'customer_id' ? 'Customer ID' : 
                 filterType === 'loan_id' ? 'Loan ID' : 'EMI ID'}
              </label>
              <input
                type="number"
                className="form-control form-control-sm"
                value={filterValue}
                onChange={e => setFilterValue(e.target.value)}
                onKeyDown={handleKeyDown}
              />
            </div>
          )}

          {/* Date Range */}
          <div className="col-auto">
            <label className="form-label">From Date</label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={fromDate}
              onChange={e => setFromDate(e.target.value)}
            />
          </div>

          <div className="col-auto">
            <label className="form-label">To Date</label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={toDate}
              onChange={e => setToDate(e.target.value)}
            />
          </div>

          {/* Search Button */}
          <div className="col-auto">
            <button
  className="btn btn-sm btn-primary mt-1"
  onClick={() => fetchEmis(1)}
>
  Search
</button>

          </div>

        </div>
         {/* Export Button */}
                    <div className="d-flex justify-content-end mt-2">
          <Button
            color="success"
            size="sm"
            onClick={exportAllByPaging}
            style={{ fontWeight: "500" }}
          >
            Export to Excel
          </Button>
        </div>
      </div>

     
            

      {/* EMI Table */}
      <div className="table-responsive" style={{ overflowX: 'auto', whiteSpace: 'nowrap' }}>
        <table className="table tablestyle table-sm table-bordered table-hover align-middle">
          <thead className="table-dark">
            <tr>
              <th>S No.</th>
              <th>EMI ID</th>
              <th>Customer ID</th>
              <th>Customer Name</th>
              <th>Fintech Name</th>
              <th>Phone Number</th>
              <th>Loan ID</th>
              <th>DPD</th>
              <th>EMI Date</th>
              <th>EMI Amount</th>
              <th>EMI Number</th>
              <th>EMI Status</th>
              <th>Outstanding Amount</th>
              <th>Outstanding Interest</th>
              <th>Outstanding Principal</th>
              <th>Action</th>

            </tr>
          </thead>
          <tbody>
             {emis.length > 0 ? (
    emis.map((emi, index) => (
                <tr key={emi.emi_id || index}>
                  <td>{(currentPage - 1) * rowsPerPage + index + 1}</td>
                  <td>{emi.emi_id || '-'}</td>
                  <td>{emi.customer_id || '-'}</td>
                  <td>{emi.customer_name || '-'}</td>
                  <td>{emi.fintech_name || '-'}</td>
                  <td>{emi.phone_number || '-'}</td>
                  <td>{emi.loan_id || '-'}</td>
                  <td>{emi.dpd || '-'}</td>
                  <td>{emi.emi_date ? new Date(emi.emi_date).toLocaleDateString() : '-'}</td>
                  <td>{emi.full_emi_amount || '-'}</td>
                  <td>{emi.emi_number || '-'}</td>
                  <td>{emi.emi_status_value || emi.emi_status || '-'}</td>
                  <td>{emi.outstanding_amount || '-'}</td>
                  <td>{emi.outstanding_interest || '-'}</td>
                  <td>{emi.outstanding_principal || '-'}</td>
                  <td>
                  <Button
                    size="sm"
                    color="info"
                    onClick={() => openPaymentPopup(emi)}
                  >
                    Pay
                  </Button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="12" className="text-center text-muted">
                  No EMI schedules found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

     <div className="d-flex justify-content-center mt-3 gap-2">
  <button
    className="btn btn-sm btn-outline-primary"
    disabled={currentPage === 1}
    onClick={() => fetchEmis(currentPage - 1)}
  >
    Prev
  </button>

  <span className="align-self-center">
    Page {currentPage}
  </span>

    {hasNextPage && <span className="text-muted">...</span>}

  <button
    className="btn btn-sm btn-outline-primary"
    disabled={!hasNextPage}
    onClick={() => fetchEmis(currentPage + 1)}
  >
    Next
  </button>
</div>

      <Modal isOpen={modalOpen} toggle={() => setModalOpen(!modalOpen)} size="sm">
  <ModalHeader toggle={() => setModalOpen(!modalOpen)}>
    Make Payment
  </ModalHeader>
  <ModalBody>
    {selectedRow && (
      <>
        <p><strong>Customer ID:</strong> {selectedRow.customer_id}</p>
        <p><strong>Loan ID:</strong> {selectedRow.loan_id}</p>

        <Form>
          <Label className="fw-bold">Amount</Label>
          <Input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            bsSize="sm"
          />

          <div className="d-flex justify-content-between mt-3">
            <Button
              color="info"
              size="sm"
              onClick={handleGenerateLink}
              disabled={payLoading}
            >
              {payLoading ? "Processing..." : "Generate Link"}
            </Button>

            <Button
              color="success"
              size="sm"
              onClick={handleSendLink}
              disabled={payLoading}
            >
              {payLoading ? "Processing..." : "Send Link"}
            </Button>
          </div>

          {paymentLink && (
            <div className="mt-3 p-2 border rounded bg-light">
              <small className="text-break">{paymentLink}</small>
              <Button
                color="secondary"
                size="sm"
                className="mt-2"
                onClick={handleCopy}
              >
                Copy
              </Button>
            </div>
          )}
        </Form>
      </>
    )}
  </ModalBody>
</Modal>

<ToastContainer />

    </div>
  );
};

export default TodayEmiSchedule;
